import React, { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { setActiveMeetingIsNarrowWindowAction } from 'flow/actions/meeting-actions';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import useOwnSpeakingDetection from 'flow/hooks/useOwnSpeakingDetection';
import {
    getLocalCallIds,
    isSessionEstablished,
} from 'flow/selectors/call-selectors';
import {
    selectActiveMeetingViewIsFullscreen,
    selectActiveMeetingViewIsMinimized,
    selectRightPanelActive,
} from 'flow/selectors/meeting-selectors';
import { ONE_PANE_WIDTH_LIMIT } from 'flow/utils/meeting-constants';
import MeetingCallNotification from './call/MeetingCallNotification';
import MeetingError from './MeetingError';
import MeetingRightPanel from './MeetingRightPanel';
import MeetingVideoGrid from './MeetingVideoGrid';

/**
 * Tracks the window width and keeps the meeting's "narrow window" (one-pane)
 * flag in sync as it crosses ONE_PANE_WIDTH_LIMIT. Returns the current width.
 */
const useNarrowWindowTracking = (): number => {
    const dispatch = useAppDispatch();
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    const setIsNarrowWindow = (isNarrowWindow: boolean) =>
        dispatch(setActiveMeetingIsNarrowWindowAction(isNarrowWindow));

    const handleWindowSizeOnLoad = () => {
        if (window.innerWidth <= ONE_PANE_WIDTH_LIMIT) {
            setIsNarrowWindow(true);
        } else if (window.innerWidth > ONE_PANE_WIDTH_LIMIT) {
            setIsNarrowWindow(false);
        }
        setWindowWidth(window.innerWidth);
    };

    let resizeTimer: NodeJS.Timeout;

    const handleResizeWindow = () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
            if (
                windowWidth > ONE_PANE_WIDTH_LIMIT &&
                window.innerWidth <= ONE_PANE_WIDTH_LIMIT
            ) {
                setIsNarrowWindow(true);
            } else if (
                windowWidth < ONE_PANE_WIDTH_LIMIT &&
                window.innerWidth > ONE_PANE_WIDTH_LIMIT
            ) {
                setIsNarrowWindow(false);
            }
            setWindowWidth(window.innerWidth);
        }, 500);
    };

    // No deps: re-bind each render so the resize handler closes over the latest
    // windowWidth (preserves the original in-component behaviour).
    useEffect(() => {
        handleWindowSizeOnLoad();
        window.addEventListener('resize', handleResizeWindow);

        return () => {
            window.removeEventListener('resize', handleResizeWindow);
        };
    });

    return windowWidth;
};

// The header and footer are fixed overlays in fullscreen/one-pane views, so the
// canvas pins to the viewport *between* them — everything inside (video grid and
// both side panels) is then framed below the header and above the footer with no
// per-piece offsets. In the dock it's an in-flow fixed 360px panel.
const getCanvasContainerClass = (
    isMinimizedView: boolean,
    headerOverlays: boolean,
): string =>
    twMerge(
        // justify-end keeps a side panel pinned to the end while it slides open.
        // It only bites when the row has free space — i.e. the narrow/windowed
        // takeover where the main area is hidden; in wide mode the main area
        // fills the row, so this is a no-op there.
        // bg-black (not gray-900) so the backdrop matches the stage and panels.
        // Otherwise it shows as a dark-gray flash in the area a panel is sliding
        // over while the main video is hidden (the narrow/dock takeover).
        'flex h-full w-full flex-row justify-end overflow-x-hidden bg-black text-white',
        isMinimizedView && 'top-0 h-15',
        headerOverlays &&
            // h-auto so the top/bottom insets define the height (the base
            // h-full would otherwise win and overflow past the footer).
            'fixed start-0 end-0 top-[var(--spacing-meeting-header)] bottom-[var(--spacing-meeting-footer)] h-auto z-30',
        !isMinimizedView &&
            !headerOverlays &&
            // The dock is a fixed 360px panel, but it lives inside the call
            // list's `px-2` scroll container, so its static position is inset
            // 8px on the start — which shifts the whole panel off-centre and
            // overflows the end. Cancel that one padding step so the panel
            // aligns to the 360px column. Bound the height to the area between
            // the (in-flow) header and footer — otherwise the base h-full runs
            // the whole viewport and a full-height panel's footer falls off.
            'z-inherit fixed -ms-2 h-[calc(100vh-var(--spacing-meeting-header)-var(--spacing-meeting-footer))] w-90',
    );

// The canvas is height-bounded in every non-minimized mode (framed between the
// fixed header/footer when overlaying, or an explicit calc in the dock), so the
// grid just fills it. p-3 frames the video uniformly; the panel's matching 12px
// frame + no leading margin makes the video↔panel gap a single clean 12px.
const getGridWrapperClass = (isMinimizedView: boolean): string =>
    isMinimizedView ? 'h-[60px] justify-start ps-3' : 'h-full p-3';

const MeetingVideoCanvas: React.FC = () => {
    const isFullscreenView = useAppSelector(
        selectActiveMeetingViewIsFullscreen,
    );
    const isMinimizedView = useAppSelector(selectActiveMeetingViewIsMinimized);
    const rightPanelActive = useAppSelector(selectRightPanelActive);
    const localCallIds = useAppSelector(getLocalCallIds);
    const phoneCallId = localCallIds[0];
    const phoneCallIsEstablished = useAppSelector((state) =>
        isSessionEstablished(state, phoneCallId),
    );

    const windowWidth = useNarrowWindowTracking();
    // Detects own-user speaking and reports start/stop edges (drives the frame).
    useOwnSpeakingDetection();

    const headerOverlays =
        isFullscreenView || windowWidth <= ONE_PANE_WIDTH_LIMIT;
    // An open side panel takes over the whole meeting area (the main video is
    // hidden) whenever there isn't room for both — the narrow window or the
    // side-pane dock. In that case the panel fills its container rather than
    // sitting at the fixed side-panel width.
    const panelTakesOver =
        !isFullscreenView || windowWidth <= ONE_PANE_WIDTH_LIMIT;
    const gridWrapperClass = getGridWrapperClass(isMinimizedView);

    return (
        <div
            className={getCanvasContainerClass(isMinimizedView, headerOverlays)}
        >
            <MeetingError />
            <div
                className={twMerge(
                    'relative w-full bg-black',
                    rightPanelActive && panelTakesOver && 'hidden',
                )}
            >
                <div
                    className={twMerge(
                        'flex w-full flex-row items-center justify-center bg-black',
                        gridWrapperClass,
                    )}
                >
                    <MeetingVideoGrid />
                </div>
                {phoneCallId && !phoneCallIsEstablished ? (
                    <div className="absolute end-1.5 top-16 z-20">
                        <MeetingCallNotification callId={phoneCallId} />
                    </div>
                ) : null}
            </div>
            <MeetingRightPanel fill={panelTakesOver} />
        </div>
    );
};

export default MeetingVideoCanvas;
